import { SkeletonList } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Tree,
  TreeItem,
  TreeItemLayout,
  __css,
  mergeClasses,
  tokens,
  type TreeOpenChangeData,
  type TreeOpenChangeEvent,
} from '@fluentui/react-components';
import { Folder20Regular, FolderOpen20Regular, FolderOpen20Filled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useFolderChildren } from './api';
import type { Folder } from './model';

const useStyles = __css({"root":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r"},"all":{"Brf1p80":"fbhxue7"},"selectedName":{"Bhrd7zp":"fl43uef"},"name":{"Btd35i7":"fjksvth"},"muted":{"sj55zd":"f11d4kpn"}});

interface SharedProps {
  spaceId: string;
  selectedId: string | null;
  openItems: ReadonlySet<string>;
}

interface NodesProps extends SharedProps {
  parentId: string | null;
  /** Children are fetched only once their parent is open (lazy tree). */
  enabled: boolean;
  onSelect: (id: string) => void;
}

/** The children of one folder, fetched when the folder is opened. */
function Nodes({ parentId, enabled, onSelect, ...shared }: NodesProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const children = useFolderChildren(shared.spaceId, parentId, enabled);

  if (children.isError) {
    return (
      <TreeItem itemType="leaf" value={`error:${String(parentId)}`}>
        <TreeItemLayout>
          <span className={styles.muted}>{t('tree.childrenFailed')}</span>
        </TreeItemLayout>
      </TreeItem>
    );
  }
  return (
    <>
      {children.data?.items.map((folder) => (
        <Node key={folder.id} folder={folder} onSelect={onSelect} {...shared} />
      ))}
    </>
  );
}

interface NodeProps extends SharedProps {
  folder: Folder;
  onSelect: (id: string) => void;
}

function Node({ folder, onSelect, ...shared }: NodeProps) {
  const styles = useStyles();
  const open = shared.openItems.has(folder.id);
  const selected = shared.selectedId === folder.id;
  const Icon = open ? FolderOpen20Filled : Folder20Regular;
  return (
    <TreeItem
      itemType={folder.hasChildren ? 'branch' : 'leaf'}
      value={folder.id}
      aria-current={selected ? 'true' : undefined}
      onClick={() => {
        onSelect(folder.id);
      }}
    >
      <TreeItemLayout iconBefore={<Icon />}>
        <bdi className={mergeClasses(styles.name, selected && styles.selectedName)}>
          {folder.name}
        </bdi>
      </TreeItemLayout>
      {folder.hasChildren ? (
        <Tree aria-label={folder.name}>
          <Nodes parentId={folder.id} enabled={open} onSelect={onSelect} {...shared} />
        </Tree>
      ) : null}
    </TreeItem>
  );
}

export interface FolderTreeProps {
  spaceId: string;
  /** The open folder; null is the root ("all folders"). */
  selectedId: string | null;
  openItems: ReadonlySet<string>;
  onOpenChange: (openItems: ReadonlySet<string>) => void;
  onSelect: (id: string | null) => void;
}

/**
 * The folder tree of the space: root folders first, a folder's children loaded when it is opened.
 * Arrow keys, Home/End and Enter work as in any tree; the open folder is marked by `aria-current`
 * and by its bold name (colour is never alone).
 */
export function FolderTree({
  spaceId,
  selectedId,
  openItems,
  onOpenChange,
  onSelect,
}: FolderTreeProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const roots = useFolderChildren(spaceId, null);

  const handleOpenChange = (_event: TreeOpenChangeEvent, data: TreeOpenChangeData) => {
    onOpenChange(new Set(Array.from(data.openItems, String)));
  };

  return (
    <nav className={styles.root} aria-label={t('tree.label')}>
      <Button
        appearance={selectedId === null ? 'secondary' : 'subtle'}
        className={styles.all}
        icon={<FolderOpen20Regular />}
        aria-current={selectedId === null ? 'true' : undefined}
        onClick={() => {
          onSelect(null);
        }}
      >
        {t('tree.all')}
      </Button>
      {roots.isError ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{t('tree.loadFailed')}</MessageBarBody>
          <MessageBarActions>
            <Button
              onClick={() => {
                void roots.refetch();
              }}
            >
              {t('common:actions.retry')}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : !roots.data ? (
        <SkeletonList count={4} />
      ) : roots.data.items.length > 0 ? (
        <Tree aria-label={t('tree.label')} openItems={openItems} onOpenChange={handleOpenChange}>
          {roots.data.items.map((folder) => (
            <Node
              key={folder.id}
              folder={folder}
              spaceId={spaceId}
              selectedId={selectedId}
              openItems={openItems}
              onSelect={onSelect}
            />
          ))}
        </Tree>
      ) : null}
    </nav>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/folder-tree.griffel.css";